import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X, RefreshCw, Trash2, Ban, Unlock, AlertTriangle, Clock, Globe, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { MODAL_OVERLAY, SLIDE_UP_LG } from "../lib/animations";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const SecurityDashboard = ({ isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockForm, setBlockForm] = useState({ type: null, value: "", reason: "" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/dashboard`);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load security data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, fetchData]);

  const handleUnblockIP = async (ip) => {
    try {
      await axios.post(`${API}/security/unblock-ip`, { ip });
      toast.success(`IP ${ip} unblocked`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unblock IP");
    }
  };

  const handleUnblockUser = async (discordId) => {
    try {
      await axios.post(`${API}/security/unblock-user`, { discord_id: discordId });
      toast.success("User unblocked");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unblock user");
    }
  };

  const handleBlock = async () => {
    if (!blockForm.value.trim()) return toast.error("Value required");
    try {
      if (blockForm.type === "ip") {
        await axios.post(`${API}/security/block-ip`, { ip: blockForm.value, reason: blockForm.reason || "Manual block" });
      } else {
        await axios.post(`${API}/security/block-user`, { discord_id: blockForm.value, reason: blockForm.reason || "Manual block" });
      }
      toast.success("Blocked successfully");
      setBlockForm({ type: null, value: "", reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to block");
    }
  };

  const handleClearEvents = async () => {
    if (!window.confirm("Clear all security events?")) return;
    try {
      await axios.delete(`${API}/security/events`);
      toast.success("Events cleared");
      fetchData();
    } catch (error) {
      toast.error("Failed to clear events");
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical": return "text-red-400 bg-red-500/20 border-red-500/30";
      case "warning": return "text-amber-400 bg-amber-500/20 border-amber-500/30";
      default: return "text-blue-400 bg-blue-500/20 border-blue-500/30";
    }
  };

  const getEventIcon = (type) => {
    if (type.includes("BLOCK")) return <Ban className="w-4 h-4" />;
    if (type.includes("RATE")) return <Clock className="w-4 h-4" />;
    if (type.includes("LOCKOUT")) return <Shield className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    const d = new Date(timestamp);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (!isOpen) return null;

  return (
    <motion.div
      {...MODAL_OVERLAY}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="glass-card w-full max-w-5xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="security-dashboard"
      >
        {/* Header */}
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-none bg-red-500/20 border border-red-500/30">
                <Shield className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-white">Security Center</h2>
                <p className="text-zinc-400 text-sm">Anti-raid protection & threat monitoring</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchData} className="p-2 hover:bg-zinc-800 rounded-none" data-testid="refresh-security">
                <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-none" data-testid="close-security">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] scrollbar-thin space-y-6">
          {loading && !data ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Loading security data...</p>
            </div>
          ) : data ? (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-4">
                <div className="glass-card rounded-none p-4 border border-red-500/20">
                  <p className="text-xs text-zinc-500 mb-1">Blocked IPs</p>
                  <p className="text-2xl font-bold font-heading text-red-400">{data.blocked_ips?.length || 0}</p>
                </div>
                <div className="glass-card rounded-none p-4 border border-amber-500/20">
                  <p className="text-xs text-zinc-500 mb-1">Blocked Users</p>
                  <p className="text-2xl font-bold font-heading text-amber-400">{data.blocked_users?.length || 0}</p>
                </div>
                <div className="glass-card rounded-none p-4 border border-orange-500/20">
                  <p className="text-xs text-zinc-500 mb-1">Temp Lockouts</p>
                  <p className="text-2xl font-bold font-heading text-orange-400">{data.temp_blocked_ips?.length || 0}</p>
                </div>
                <div className="glass-card rounded-none p-4 border border-blue-500/20">
                  <p className="text-xs text-zinc-500 mb-1">Recent Events</p>
                  <p className="text-2xl font-bold font-heading text-blue-400">{data.recent_events?.length || 0}</p>
                </div>
              </div>

              {/* Block Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setBlockForm({ type: blockForm.type === "ip" ? null : "ip", value: "", reason: "" })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-none border transition-colors text-sm font-medium ${
                    blockForm.type === "ip" ? "bg-red-500/20 border-red-500/50 text-red-400" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"
                  }`}
                  data-testid="block-ip-toggle"
                >
                  <Globe className="w-4 h-4" /> Block IP
                </button>
                <button
                  onClick={() => setBlockForm({ type: blockForm.type === "user" ? null : "user", value: "", reason: "" })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-none border transition-colors text-sm font-medium ${
                    blockForm.type === "user" ? "bg-red-500/20 border-red-500/50 text-red-400" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"
                  }`}
                  data-testid="block-user-toggle"
                >
                  <Ban className="w-4 h-4" /> Block User
                </button>
              </div>

              {/* Block Form */}
              <AnimatePresence>
                {blockForm.type && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="glass-card rounded-none p-4 border border-red-500/20 flex gap-3 items-end">
                      <div className="flex-1">
                        <label className="block text-xs text-zinc-500 mb-1">
                          {blockForm.type === "ip" ? "IP Address" : "Discord ID"}
                        </label>
                        <input
                          type="text"
                          value={blockForm.value}
                          onChange={(e) => setBlockForm({ ...blockForm, value: e.target.value })}
                          placeholder={blockForm.type === "ip" ? "e.g. 192.168.1.1" : "e.g. 227845414378930176"}
                          className="input-field w-full"
                          data-testid="block-input"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-zinc-500 mb-1">Reason</label>
                        <input
                          type="text"
                          value={blockForm.reason}
                          onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                          placeholder="Reason for blocking"
                          className="input-field w-full"
                          data-testid="block-reason"
                        />
                      </div>
                      <button onClick={handleBlock} className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-6 rounded transition-colors" data-testid="confirm-block">
                        Block
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Blocked IPs Table */}
              {(data.blocked_ips?.length > 0 || data.temp_blocked_ips?.length > 0) && (
                <div className="glass-card rounded-none overflow-hidden">
                  <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-red-400" />
                    <h3 className="text-lg font-heading font-bold text-white">Blocked IPs</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="table-header">
                        <th className="text-left py-3 px-4">IP Address</th>
                        <th className="text-left py-3 px-4">Reason</th>
                        <th className="text-left py-3 px-4">Blocked At</th>
                        <th className="text-left py-3 px-4">Type</th>
                        <th className="text-right py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.blocked_ips?.map((block, i) => (
                        <tr key={`perm-${i}`} className="table-row">
                          <td className="py-3 px-4 font-mono text-sm text-white">{block.ip}</td>
                          <td className="py-3 px-4 text-zinc-400 text-sm">{block.reason}</td>
                          <td className="py-3 px-4 text-zinc-500 text-sm">{formatTime(block.blocked_at)}</td>
                          <td className="py-3 px-4"><span className="px-2 py-1 rounded text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30">Permanent</span></td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => handleUnblockIP(block.ip)} className="p-2 hover:bg-emerald-500/20 rounded-none" title="Unblock" data-testid={`unblock-ip-${i}`}>
                              <Unlock className="w-4 h-4 text-emerald-400" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {data.temp_blocked_ips?.map((block, i) => (
                        <tr key={`temp-${i}`} className="table-row opacity-70">
                          <td className="py-3 px-4 font-mono text-sm text-white">{block.ip}</td>
                          <td className="py-3 px-4 text-zinc-400 text-sm">Failed auth lockout</td>
                          <td className="py-3 px-4 text-zinc-500 text-sm">Expires: {formatTime(block.expires)}</td>
                          <td className="py-3 px-4"><span className="px-2 py-1 rounded text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">Temporary</span></td>
                          <td className="py-3 px-4 text-right">
                            <span className="text-zinc-600 text-xs">Auto-expires</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Blocked Users Table */}
              {data.blocked_users?.length > 0 && (
                <div className="glass-card rounded-none overflow-hidden">
                  <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
                    <User className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-heading font-bold text-white">Blocked Users</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="table-header">
                        <th className="text-left py-3 px-4">Discord ID</th>
                        <th className="text-left py-3 px-4">Reason</th>
                        <th className="text-left py-3 px-4">Blocked At</th>
                        <th className="text-left py-3 px-4">Blocked By</th>
                        <th className="text-right py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.blocked_users?.map((block, i) => (
                        <tr key={block.discord_id || `blocked-user-${i}`} className="table-row">
                          <td className="py-3 px-4 font-mono text-sm text-white">{block.discord_id}</td>
                          <td className="py-3 px-4 text-zinc-400 text-sm">{block.reason}</td>
                          <td className="py-3 px-4 text-zinc-500 text-sm">{formatTime(block.blocked_at)}</td>
                          <td className="py-3 px-4 text-zinc-500 text-sm">{block.blocked_by}</td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => handleUnblockUser(block.discord_id)} className="p-2 hover:bg-emerald-500/20 rounded-none" title="Unblock" data-testid={`unblock-user-${i}`}>
                              <Unlock className="w-4 h-4 text-emerald-400" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Security Events Log */}
              <div className="glass-card rounded-none overflow-hidden">
                <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-heading font-bold text-white">Security Events</h3>
                    <span className="text-xs text-zinc-500">({data.recent_events?.length || 0})</span>
                  </div>
                  {data.recent_events?.length > 0 && (
                    <button onClick={handleClearEvents} className="flex items-center gap-1 px-3 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-xs" data-testid="clear-events">
                      <Trash2 className="w-3 h-3" /> Clear
                    </button>
                  )}
                </div>
                <div className="max-h-[300px] overflow-y-auto scrollbar-thin">
                  {!data.recent_events?.length ? (
                    <div className="p-8 text-center">
                      <Shield className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-500 text-sm">No security events</p>
                      <p className="text-zinc-600 text-xs mt-1">All clear — no threats detected</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-800/50">
                      {data.recent_events.map((event, i) => (
                        <div key={event.id || `event-${i}`} className="p-4 hover:bg-zinc-800/20 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-none border ${getSeverityColor(event.severity)}`}>
                              {getEventIcon(event.event_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${getSeverityColor(event.severity)}`}>
                                  {event.event_type}
                                </span>
                              </div>
                              <p className="text-sm text-zinc-300 mb-1">{event.detail}</p>
                              <div className="flex items-center gap-3 text-xs text-zinc-500">
                                {event.ip && <span>IP: {event.ip}</span>}
                                {event.discord_id && <span>User: {event.discord_id}</span>}
                                <span>{formatTime(event.timestamp)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Protection Status */}
              <div className="glass-card rounded-none p-4 border border-emerald-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-heading font-bold text-white">Active Protections</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: "Rate Limiting", desc: "60 req/min per IP", active: true },
                    { label: "Auth Lockout", desc: "5 fails → 15 min ban", active: true },
                    { label: "Bulk Action Guard", desc: "Auto-block on mass ops", active: true },
                    { label: "Raid Detection", desc: "Auto-block + alert", active: true },
                  ].map((p) => (
                    <div key={p.label} className="p-3 rounded-none bg-emerald-500/5 border border-emerald-500/20">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-sm font-medium text-emerald-400">{p.label}</span>
                      </div>
                      <p className="text-xs text-zinc-500">{p.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SecurityDashboard;
